import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Camera, ImagePlus, Trash2, Loader2, FileImage } from "lucide-react";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const MAX_CARGO = 10;

/**
 * BufferedPhotoPicker — pre-upload photo staging area.
 *
 * Unlike `ShipmentPhotoUploader`, this component never hits the API. It just
 * lets the user pick/take photos (with client-side compression) and keeps them
 * as `File` objects in local state via the controlled `files` + `onChange`
 * props. The parent posts the shipment and uploads these files afterwards.
 *
 * Props:
 *  • kind      : "document" | "cargo" — determines max count + labels
 *  • files     : File[]  — controlled list
 *  • onChange  : (next: File[]) => void
 */
export default function BufferedPhotoPicker({ kind = "cargo", files = [], onChange }) {
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const isDoc = kind === "document";
  const limit = isDoc ? 1 : MAX_CARGO;
  const canAddMore = files.length < limit;

  const addFiles = async (fileList) => {
    const raw = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (!raw.length) return;
    const remaining = limit - files.length;
    if (raw.length > remaining) {
      toast.warning(`En fazla ${remaining} fotoğraf daha ekleyebilirsiniz`);
    }
    const toAdd = raw.slice(0, remaining);
    if (!toAdd.length) return;

    setBusy(true);
    try {
      const compressed = await Promise.all(toAdd.map((f) => compressImage(f)));
      onChange?.([...files, ...compressed]);
    } catch (err) {
      toast.error("Fotoğraf sıkıştırma başarısız: " + (err?.message || ""));
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const remove = (i) => {
    const next = files.filter((_, idx) => idx !== i);
    onChange?.(next);
  };

  const previewUrl = (f) => URL.createObjectURL(f);

  return (
    <div className="space-y-2" data-testid={`buffered-photos-${kind}`}>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase text-slate-500 font-mono flex items-center gap-1.5">
          <FileImage className="w-3.5 h-3.5" />
          {isDoc ? "Sevk Evrağı Fotoğrafı" : `Kargo Fotoğrafları (${files.length}/${MAX_CARGO})`}
        </Label>
        {canAddMore && (
          <div className="flex gap-1.5">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
              data-testid={`buffered-photo-camera-${kind}`}
            />
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              multiple={!isDoc}
              className="hidden"
              onChange={(e) => addFiles(e.target.files)}
              data-testid={`buffered-photo-gallery-${kind}`}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => cameraRef.current?.click()}
              className="border-emerald-800 bg-emerald-950/30 text-emerald-300 hover:bg-emerald-900/60 rounded-sm h-7 text-xs"
            >
              <Camera className="w-3.5 h-3.5 mr-1" /> Kamera
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => galleryRef.current?.click()}
              className="border-blue-800 bg-blue-950/30 text-blue-300 hover:bg-blue-900/60 rounded-sm h-7 text-xs"
            >
              <ImagePlus className="w-3.5 h-3.5 mr-1" /> Galeri
            </Button>
          </div>
        )}
      </div>

      {busy && (
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
          Fotoğraflar sıkıştırılıyor…
        </div>
      )}

      {files.length > 0 && (
        <div className={`grid gap-2 ${isDoc ? "grid-cols-1" : "grid-cols-3 sm:grid-cols-5"}`}>
          {files.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="relative group border border-slate-800 rounded-sm overflow-hidden bg-slate-950 aspect-square"
              data-testid={`buffered-photo-thumb-${kind}-${i}`}
            >
              <img src={previewUrl(f)} alt={f.name} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(i)}
                title="Kaldır"
                data-testid={`buffered-photo-remove-${kind}-${i}`}
                className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-red-900/80 hover:bg-red-800 text-white rounded-sm p-1"
              >
                <Trash2 className="w-3 h-3" />
              </button>
              <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-[9px] text-slate-300 font-mono px-1.5 py-0.5 truncate">
                {(f.size / 1024).toFixed(0)} KB
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && (
        <div className="text-[10px] text-slate-500 font-mono">
          {isDoc
            ? "Kamera ile çekin veya galeriden 1 sevk evrağı fotoğrafı seçin."
            : "Kargo/paket fotoğrafları çekin (max 10 adet). Sıkıştırma otomatik yapılır."}
        </div>
      )}
    </div>
  );
}
